import { jwtDecode } from "jwt-decode";
import * as Constants from "./Constants";

/**
 * True when `jwtToken`'s own `exp` claim is at/before `nowMs`. False for a missing/unparseable
 * token or one with no `exp` claim — callers should let the server's 401 decide in that case
 * rather than force a logout on a token we can't actually read. Pure and unit-tested, mirroring
 * the Android client's `util/SessionUtils.isSessionExpired` (same reasoning: decode locally, no
 * network round trip, `nowMs` injectable for deterministic tests).
 */
export function isJwtExpired(jwtToken, nowMs = Date.now()) {
  if (!jwtToken) return false;
  try {
    const decoded = jwtDecode(jwtToken);
    if (!decoded?.exp) return false;
    return nowMs >= decoded.exp * 1000;
  } catch {
    return false;
  }
}

/**
 * Epoch ms the *stored* access token expires at, or null when there's nothing parseable to check
 * (not logged in, corrupted storage, no `exp` claim). Reads `Constants.LOGGED_IN_USER` directly
 * (the login response JSON) rather than `utility.getAuthToken()`'s already-expired-token side
 * effect (which clears storage as a side effect of merely reading it) — this is a read-only probe.
 */
export function getStoredAccessTokenExpiryMs() {
  try {
    const raw = localStorage.getItem(Constants.LOGGED_IN_USER);
    if (!raw) return null;
    const jwtToken = JSON.parse(raw)?.jwtToken;
    if (!jwtToken) return null;
    const decoded = jwtDecode(jwtToken);
    return decoded?.exp ? decoded.exp * 1000 : null;
  } catch {
    return null;
  }
}
